import type {
  ContentOutputMetadata,
  ContentUnitOutput,
  DefinitionOutput,
  ForOutputMetadata,
  ForUnitOutput,
  ImportUnitOutput,
  PluginOutput,
  UseUnitOutput,
} from "@magic-md/core";
import type {
  ExecutionCascadeEvent,
  ExecutionCompletedEvent,
  ExecutionFailedEvent,
  ExecutionIterationCompletedEvent,
  ExecutionJobChangedEvent,
  ExecutionProgressEvent,
  ExecutionStartedEvent,
} from "@magic-md/engine-client/protocol";
import { useEditorStore } from "./editor-store";
import {
  applyCascadeRunningTargets,
  getDefinitionPluginName,
  hasPendingRunAllForSession,
  logCompletedOutputError,
  mergeForIterationEvents,
  routeBackgroundExecution,
  routeBackgroundIterationBatch,
  type ExecutionProjectionStore,
} from "./execution-notification-projection";
import {
  applyCompletedStateForNonDefinitionUnit,
  applyFailedStateForNonDefinitionUnit,
  applyStartedStateForNonDefinitionUnit,
  isExecutionCancelledMessage,
  phaseLabel,
  runningStarts,
} from "./execution-state-transitions";
import { cloneExecutionMapForUnitType } from "./execution-session-projection";
import { useLogStore } from "./log-store";
import type { NotificationBus } from "./notification-bus";
import { tabSessionRegistry } from "./tab-session-registry";

export const EXECUTION_NOTIFICATION_BATCH_MS = 16;

export interface ExecutionNotificationHandlerDependencies {
  syncBackgroundRunningTabs: () => void;
  getActiveTabId: () => string | null | undefined;
}

/**
 * Owns the execution notification domain and its frame-sized batching queues.
 * The returned disposer clears both the bus registration and pending timers.
 */
export function registerExecutionNotificationHandlers(
  bus: NotificationBus,
  store: ExecutionProjectionStore,
  dependencies: ExecutionNotificationHandlerDependencies,
): () => void {
  const { syncBackgroundRunningTabs, getActiveTabId } = dependencies;
  const set = store.setState.bind(store);
  const get = store.getState.bind(store);
  const pendingProgress = new Map<string, ExecutionProgressEvent>();
  const pendingIterations = new Map<
    string,
    ExecutionIterationCompletedEvent[]
  >();
  const pendingBackgroundIterations: ExecutionIterationCompletedEvent[] = [];
  let batchTimer: ReturnType<typeof setTimeout> | null = null;

  const flushPendingExecutionUpdates = () => {
    if (batchTimer) {
      clearTimeout(batchTimer);
      batchTimer = null;
    }

    const currentSessionId = get().sessionId;
    const foregroundProgress: ExecutionProgressEvent[] = [];
    const backgroundProgress: ExecutionProgressEvent[] = [];
    for (const event of pendingProgress.values()) {
      if (event.sessionId && event.sessionId !== currentSessionId) {
        backgroundProgress.push(event);
      } else {
        foregroundProgress.push(event);
      }
    }
    pendingProgress.clear();

    const foregroundIterations = new Map<
      string,
      ExecutionIterationCompletedEvent[]
    >();
    for (const [unitId, events] of pendingIterations) {
      for (const event of events) {
        if (event.sessionId && event.sessionId !== currentSessionId) {
          pendingBackgroundIterations.push(event);
          continue;
        }
        const unitEvents = foregroundIterations.get(unitId) ?? [];
        unitEvents.push(event);
        foregroundIterations.set(unitId, unitEvents);
      }
    }
    pendingIterations.clear();

    if (foregroundProgress.length > 0 || foregroundIterations.size > 0) {
      set((state) => {
        const updates: Partial<typeof state> = {};

        if (foregroundProgress.length > 0) {
          const next = new Map(state.executions);
          for (const event of foregroundProgress) {
            const prev = next.get(event.unitId);
            if (!prev || prev.status !== "running") continue;
            next.set(event.unitId, {
              ...prev,
              progress: {
                message: event.detail ?? phaseLabel(event.phase),
                percent: event.percent,
                partialOutput: event.partialOutput,
                updatedAt: Date.now(),
              },
            });
          }
          updates.executions = next;
        }

        if (foregroundIterations.size > 0) {
          const next = new Map(state.forExecutions);
          for (const [unitId, events] of foregroundIterations) {
            const prev = next.get(unitId);
            if (!prev) continue;
            const iterations = mergeForIterationEvents(prev.iterations, events);
            next.set(unitId, {
              ...prev,
              completedIterations: Math.max(
                prev.completedIterations,
                iterations.length,
              ),
              iterations,
            });
          }
          updates.forExecutions = next;
        }

        return updates;
      });
    }

    for (const event of backgroundProgress) {
      routeBackgroundExecution(
        store,
        syncBackgroundRunningTabs,
        "event/execution.progress",
        event,
      );
    }
    if (pendingBackgroundIterations.length > 0) {
      const events = pendingBackgroundIterations.splice(
        0,
        pendingBackgroundIterations.length,
      );
      routeBackgroundIterationBatch(store, syncBackgroundRunningTabs, events);
    }
  };

  const scheduleExecutionUpdateFlush = () => {
    if (batchTimer) return;
    batchTimer = setTimeout(
      flushPendingExecutionUpdates,
      EXECUTION_NOTIFICATION_BATCH_MS,
    );
  };

  const disposeDomain = bus.registerDomain(
    "event/execution",
    (method, params) => {
      const eventSessionId = (params as { sessionId?: string }).sessionId;
      const eventFilePath = (params as { filePath?: string }).filePath;

      let isBackground = false;
      if (eventSessionId) {
        const editorState = useEditorStore.getState();
        const activeTabId = getActiveTabId() ?? "";
        const activeTab = activeTabId
          ? editorState.tabs.find((tab) => tab.id === activeTabId)
          : null;

        if (eventFilePath && activeTab?.filePath) {
          isBackground = eventFilePath !== activeTab.filePath;
        } else {
          const ownerTabId =
            tabSessionRegistry.getTabForSession(eventSessionId);
          if (ownerTabId) {
            isBackground = ownerTabId !== activeTabId;
          } else {
            isBackground = eventSessionId !== get().sessionId;
          }
        }
      }

      if (isBackground) {
        if (method === "event/execution.iterationCompleted") {
          pendingBackgroundIterations.push(
            params as ExecutionIterationCompletedEvent,
          );
          scheduleExecutionUpdateFlush();
          return;
        }
        const parentUnitId = (params as { parentUnitId?: string }).parentUnitId;
        if (
          method !== "event/execution.iterationStarted" &&
          parentUnitId === undefined
        ) {
          flushPendingExecutionUpdates();
        }
        routeBackgroundExecution(
          store,
          syncBackgroundRunningTabs,
          method,
          params,
        );
        return;
      }

      const parentUnitId = (params as { parentUnitId?: string }).parentUnitId;
      if (
        method !== "event/execution.progress" &&
        method !== "event/execution.iterationStarted" &&
        method !== "event/execution.iterationCompleted" &&
        parentUnitId === undefined
      ) {
        flushPendingExecutionUpdates();
      }

      switch (method) {
        case "event/execution.jobChanged": {
          const event = params as ExecutionJobChangedEvent;
          if (event.target === "unit" && event.unitId) {
            const current = get().activeExecutionIdsByUnit.get(event.unitId);
            if (event.status === "running") {
              const currentExecution = get().executions.get(event.unitId);
              const isAuxiliaryAction =
                currentExecution?.status === "running" &&
                !!currentExecution.actionName &&
                !!event.action &&
                currentExecution.actionName !== event.action;
              if (!isAuxiliaryAction && current !== event.executionId) {
                const next = new Map(get().activeExecutionIdsByUnit);
                next.set(event.unitId, event.executionId);
                set({ activeExecutionIdsByUnit: next });
              }
            } else if (current === event.executionId) {
              const next = new Map(get().activeExecutionIdsByUnit);
              next.delete(event.unitId);
              set({ activeExecutionIdsByUnit: next });
            }
          }
          if (
            event.target === "all" &&
            get().activeRunAllExecutionId === event.executionId &&
            event.status !== "running"
          ) {
            const sessionId = eventSessionId ?? get().sessionId;
            if (!sessionId || !hasPendingRunAllForSession(store, sessionId)) {
              set({
                isRunningAll: false,
                runAllProgress: null,
                activeRunAllExecutionId: null,
              });
            }
          }
          break;
        }
        case "event/execution.started": {
          const event = params as ExecutionStartedEvent;

          if (get().isRunningAll && event.progress) {
            set({
              runAllProgress: {
                current: event.progress.current,
                total: event.progress.total,
                currentId: event.unitId,
              },
            });
          }

          if (
            event.unitType === "for" ||
            event.unitType === "content" ||
            event.unitType === "import" ||
            event.unitType === "use"
          ) {
            const startedAt = Date.now();
            const totalIterations =
              event.totalIterations ?? event.progress?.total ?? 0;
            runningStarts.set(event.unitId, startedAt);
            set((state) => {
              const maps = cloneExecutionMapForUnitType(state, event.unitType);
              applyStartedStateForNonDefinitionUnit(maps, event, startedAt);
              return maps;
            });
            const message =
              event.unitType === "for"
                ? `For-loop started: ${event.unitId} (${totalIterations} iterations)`
                : event.unitType === "content"
                  ? `Content-block started: ${event.unitId}`
                  : event.unitType === "import"
                    ? `Import started: ${event.unitId}`
                    : `Use started: ${event.unitId}`;
            useLogStore.getState().debug(message, { source: "engine" });
            break;
          }

          runningStarts.set(event.unitId, Date.now());
          if (get().executions.get(event.unitId)?.status === "running") {
            break;
          }
          useLogStore
            .getState()
            .debug(`Execution started: ${event.unitId} (${event.actionName})`, {
              source: "engine",
              unitId: event.unitId,
            });
          set((state) => {
            const next = new Map(state.executions);
            const prev = next.get(event.unitId);
            if (!prev || prev.status !== "running") {
              next.set(event.unitId, {
                unitId: event.unitId,
                status: "running",
                actionName: event.actionName ?? null,
                result: null,
                output: prev?.output ?? null,
                startedAt: Date.now(),
                duration: null,
              });
            }
            return { executions: next };
          });
          break;
        }
        case "event/execution.progress": {
          const event = params as ExecutionProgressEvent;
          if (
            event.unitType === "for" ||
            event.unitType === "content" ||
            event.unitType === "import" ||
            event.unitType === "use" ||
            get().executions.get(event.unitId)?.status !== "running"
          ) {
            break;
          }
          pendingProgress.set(
            `${event.sessionId ?? ""}\u0000${event.unitId}`,
            event,
          );
          scheduleExecutionUpdateFlush();
          break;
        }
        case "event/execution.completed": {
          const event = params as ExecutionCompletedEvent;
          if (
            event.unitType === "for" ||
            event.unitType === "content" ||
            event.unitType === "import" ||
            event.unitType === "use"
          ) {
            set((state) => {
              const maps = cloneExecutionMapForUnitType(state, event.unitType);
              applyCompletedStateForNonDefinitionUnit(maps, event);
              return maps;
            });
            const message =
              event.unitType === "for"
                ? `For-loop completed: ${event.unitId} — ${((event.output as ForUnitOutput).__metadata as ForOutputMetadata | undefined)?.succeeded ?? 0}/${((event.output as ForUnitOutput).__metadata as ForOutputMetadata | undefined)?.total ?? 0} succeeded`
                : event.unitType === "content"
                  ? `Content-block completed: ${event.unitId} — ${((event.output as ContentUnitOutput).__metadata as ContentOutputMetadata | undefined)?.succeeded ?? 0}/${((event.output as ContentUnitOutput).__metadata as ContentOutputMetadata | undefined)?.total ?? 0} succeeded`
                  : event.unitType === "import"
                    ? `Import completed: ${event.unitId} — ${(event.output as ImportUnitOutput).symbols.length} symbols`
                    : `Use completed: ${event.unitId} → ${(event.output as UseUnitOutput).materializedId}`;
            useLogStore.getState().info(message, { source: "engine" });
            break;
          }

          const output =
            event.output.unitType === "definition"
              ? (event.output as DefinitionOutput).data
              : (event.output as unknown as PluginOutput);
          const hasError = !!(output as Record<string, unknown>)?.__error;
          if (hasError) {
            logCompletedOutputError(event.unitId, output);
          }
          set((state) => {
            const next = new Map(state.executions);
            const prev = next.get(event.unitId);

            if (
              prev?.status === "running" &&
              prev.actionName &&
              event.meta.actionName !== prev.actionName
            ) {
              next.set(event.unitId, {
                ...prev,
                output,
              });
              return { executions: next };
            }

            next.set(event.unitId, {
              unitId: event.unitId,
              status: hasError ? "error" : "success",
              actionName: event.meta.actionName ?? null,
              result: hasError
                ? {
                    success: false,
                    message: (
                      output as Record<string, unknown> & {
                        __error: { message: string };
                      }
                    ).__error.message,
                  }
                : { success: true, output },
              output,
              startedAt: prev?.startedAt ?? null,
              duration: event.meta.duration,
            });
            return { executions: next };
          });
          break;
        }
        case "event/execution.failed": {
          const event = params as ExecutionFailedEvent;
          if (
            event.unitType === "for" ||
            event.unitType === "content" ||
            event.unitType === "import" ||
            event.unitType === "use"
          ) {
            const cancelled = isExecutionCancelledMessage(event.error);
            set((state) => {
              const maps = cloneExecutionMapForUnitType(state, event.unitType);
              applyFailedStateForNonDefinitionUnit(maps, event, {
                cancelled,
              });
              return maps;
            });
            if (!cancelled) {
              const message =
                event.unitType === "for"
                  ? `For-loop failed: ${event.unitId} — ${event.error}`
                  : event.unitType === "content"
                    ? `Content-block failed: ${event.unitId} — ${event.error}`
                    : event.unitType === "import"
                      ? `Import failed: ${event.unitId} — ${event.error}`
                      : `Use failed: ${event.unitId} — ${event.error}`;
              useLogStore.getState().error(message, { source: "engine" });
            }
            break;
          }

          const cancelled = isExecutionCancelledMessage(event.error);
          useLogStore
            .getState()
            .error(`Execution failed: ${event.unitId} — ${event.error}`, {
              source: "engine",
              unitId: event.unitId,
              pluginName: getDefinitionPluginName(event.unitId),
            });
          set((state) => {
            const next = new Map(state.executions);
            const prev = next.get(event.unitId);

            if (
              prev?.status === "running" &&
              prev.actionName &&
              event.actionName !== prev.actionName
            ) {
              return { executions: next };
            }

            next.set(event.unitId, {
              unitId: event.unitId,
              status: cancelled ? "idle" : "error",
              actionName: event.actionName ?? null,
              result: cancelled
                ? null
                : { success: false, message: event.error },
              output: prev?.output ?? null,
              startedAt: prev?.startedAt ?? null,
              duration: prev?.startedAt ? Date.now() - prev.startedAt : null,
            });
            return { executions: next };
          });
          break;
        }
        case "event/execution.cascade": {
          const event = params as ExecutionCascadeEvent;
          useLogStore
            .getState()
            .debug(
              `Cascade from ${event.sourceUnitId} -> [${event.targetUnitIds.join(", ")}]`,
              { source: "engine" },
            );
          set((state) =>
            applyCascadeRunningTargets(state, event.targetUnitIds),
          );
          break;
        }
        case "event/execution.iterationStarted":
          break;
        case "event/execution.iterationCompleted": {
          const event = params as ExecutionIterationCompletedEvent;
          const events = pendingIterations.get(event.unitId) ?? [];
          events.push(event);
          pendingIterations.set(event.unitId, events);
          scheduleExecutionUpdateFlush();
          break;
        }
      }
    },
  );

  return () => {
    if (batchTimer) {
      clearTimeout(batchTimer);
      batchTimer = null;
    }
    pendingProgress.clear();
    pendingIterations.clear();
    pendingBackgroundIterations.length = 0;
    disposeDomain();
  };
}
